import { elementFactory } from "./factory.js";
import { playerMenu } from "./playerMenu.js";
import { tileMenu } from "./tileMenu.js";
import { navigationMenu } from "./navigationMenu.js";
import { ModalManager } from "./modalManager.js";
import { GalaxyView } from "./galaxyView.js";

export class uiManager {
    constructor(gameState) {
        this.state = gameState;

        //Default state if absent from gameState
        if (!this.state.uiState) {
            this.state.uiState = {
                isPlayerMenuActive: false,
                activePlayerSubmenu: "none",
                isTileMenuActive: false,
                activeTileSubmenu: "none", 
                isGalaxyViewActive: false
            };
        }

        this.pagesContainer = document.getElementById('pages-container');

        this.menus = {
            world: {
                gridContainer: document.getElementById("grid-exploration"),
            },

            navMenu: document.getElementById("menu-navigation"),
            player: {
                root: document.querySelector('#menu-player'),
                optionsContainer: document.querySelector('#player-menu-options'),
                submenuButtons: {
                    settings: document.querySelector('#settings-button'),
                    awakening: document.querySelector('#awakening-button'),
                    cultivation: document.querySelector('#cultivation-button'),
                    formation: document.querySelector('#formation-button'),
                    spellbook: document.querySelector('#spellbook-button'),
                },
                submenus: {
                    settings: document.querySelector('.submenu-settings'),
                    awakening: document.querySelector('.submenu-awakening'),
                    cultivation: document.querySelector('.submenu-cultivation'),
                    formation: document.querySelector('.submenu-formation'),
                    spellbook: document.querySelector('.submenu-spellbook'),
                    current: "none"
                },
                playerDropdown: {
                    button: document.querySelector('#player-dropdown-header'),
                    optionsDiv: document.querySelector('#player-dropdown-options'),
                    title: document.querySelector('#player-dropdown-title'),
                    options: {
                        settings: document.querySelector('#drop-settings-button'),
                        awakening: document.querySelector('#drop-awakening-button'),
                        cultivation: document.querySelector('#drop-cultivation-button'),
                        formation: document.querySelector('#drop-formation-button'),
                        spellbook: document.querySelector('#drop-spellbook-button')
                    }    
                },
                closePlayerMenu: document.querySelector('#close-player-menu'),
            },

            tile: {
                root: document.querySelector('#menu-tile'),
                submenuButtons: {
                    chat: document.querySelector('#chat-button'),
                    hunting: document.querySelector('#hunting-button'),
                    sanctuary: document.querySelector('#sanctuary-button'),
                    quest: document.querySelector('#quest-button')
                },
                closeButton: document.querySelector("#close-tile-menu"),
                submenus: {
                    chat: document.querySelector('.submenu-chat'),
                    hunting: document.querySelector('.submenu-hunting'),
                    sanctuary: document.querySelector('.submenu-sanctuary'),
                    quest: document.querySelector('.submenu-quest'),
                    current: "none"
                }
            },

            mainSwapViewBtn: document.querySelector('#main-toggle-view-btn'),
            galaxySwapViewBtn: document.querySelector('#galaxy-toggle-view-btn')
            //currentTileButton: document.querySelector('#current-tile-button')
        };
        
        //Settings for general UI
        this.uiSettings = {
            drawMultipleMenus: true,
            uiPersistance: false
        };

        //Instantiated menu classes
        this.tileMenu = null;
        this.playerMenu = null;
        this.navigationMenu = null;
        this.mainMenu = null;
        this.modalManager = null;
        this.galaxyView = null;

        this.lastUpdate = 0;
    }

    // Getter for local access
    get uiState() {
        return this.state.uiState;
    }

    //Initilization of ui state
    init(state) {
        //remove animations on load
        this.pagesContainer?.classList.add('no-transition');

        //Apply the uiState already loaded on state.uiState
        this.applyUiState();

        //reenable animations after initial paint
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.pagesContainer?.classList.remove('no-transition');
            });
        });
        
        //Setup event listeners
        this.setupMainMenus();

        //Setup Managers
        this.modalManager = new ModalManager();

        //Instantiate sub-menus
        this.tileMenu = new tileMenu(this.menus.tile.root, this.state);
        this.tileMenu.setupTileSubmenus();
        
        this.navigationMenu = new navigationMenu(this.menus.navMenu, this.state, this);
        this.navigationMenu.init();

        this.playerMenu = new playerMenu(this.menus.player.root, this.state, this);
        this.playerMenu.setupPlayerSubmenus();

        this.galaxyView = new GalaxyView(this.state);
    }

    applyUiState() {
        // Main menus
        this.menus.player.root?.setAttribute('data-visible', this.uiState.isPlayerMenuActive);
        this.menus.tile.root?.setAttribute('data-visible', this.uiState.isTileMenuActive);

        // Explicitly set galaxy view based on state (do NOT use swapPage here)
        const isGalaxy = Boolean(this.uiState.isGalaxyViewActive);
        this.pagesContainer?.classList.toggle('show-galaxy', isGalaxy);
        
        if (this.menus.mainSwapViewBtn && this.menus.galaxySwapViewBtn) {
            this.menus.mainSwapViewBtn.style.display = isGalaxy ? "none" : "flex";
            this.menus.galaxySwapViewBtn.style.display = isGalaxy ? "flex" : "none";
        }

        // Player submenus
        Object.entries(this.menus.player.submenus).forEach(([name, element]) => {
            if (name !== 'current' && element) {
                const isSubVisible = this.uiState.isPlayerMenuActive && this.uiState.activePlayerSubmenu === name;
                element.setAttribute('data-visible', isSubVisible);
            }  
        });

        // Tile submenus
        Object.entries(this.menus.tile.submenus).forEach(([name, element]) => {
            if (name !== 'current' && element) {
                const isSubVisible = this.uiState.isTileMenuActive && this.uiState.activeTileSubmenu === name;
                element.setAttribute('data-visible', isSubVisible);
            }   
        });
    }


    swapPage() {
        this.uiState.isGalaxyViewActive = !this.uiState.isGalaxyViewActive;
        this.applyUiState();
    }

    //Event listeners for interactions with major ui
    setupMainMenus() {
        this.menus.mainSwapViewBtn?.addEventListener("click", () => {
            this.swapPage();
        });

        this.menus.galaxySwapViewBtn?.addEventListener("click", () => {
            this.swapPage();
        });

        // Event listeners for player menu buttons 
        Object.entries(this.menus.player.submenuButtons).forEach(([key, buttonEl]) => {
            buttonEl.addEventListener("click", () => this.toggleSubmenu('player', key, 'open'));
        });

        // Event listener for opening the player dropdown
        this.menus.player.playerDropdown.button.addEventListener("click", () => {
            const options = this.menus.player.playerDropdown.optionsDiv;
            options.style.display = options.style.display === 'block' ? 'none' : 'block';
        });

        // Event listener for closing dropdown when clicking outside bounds
        window.addEventListener("click", (e) => {
            if (this.uiState.isPlayerMenuActive) {
                const button = this.menus.player.playerDropdown.button;
                const options = this.menus.player.playerDropdown.optionsDiv;
            
                if (!button.contains(e.target) && !options.contains(e.target)) {
                    options.style.display = 'none';
                }
            }
        });

        // Event listeners for options in player dropdown
        Object.entries(this.menus.player.playerDropdown.options).forEach(([key, buttonEl]) => {
            buttonEl.addEventListener("click", () => {
                this.setPlayerDropdownTitle(key);
                this.menus.player.playerDropdown.optionsDiv.style.display = 'none';
                this.toggleSubmenu('player', key, 'open');
            });
        });

        // Close buttons
        this.menus.player.closePlayerMenu.addEventListener("click", (e) => {
            e.stopPropagation();
            this.toggleMainMenu('player', 'close');
        });

        this.menus.tile.closeButton.addEventListener("click", (e) => {
            e.stopPropagation();
            this.toggleMainMenu('tile', 'close');
        });

        // Event listeners for tile submenu buttons
        Object.entries(this.menus.tile.submenuButtons).forEach(([key, buttonEl]) => {
            buttonEl.addEventListener("click", () => this.toggleSubmenu('tile', key));
        });     
    }

    //Helper function for generating text for dropdown in player menu
    setPlayerDropdownTitle(key) {
        this.menus.player.playerDropdown.title.innerText = `${key.toLocaleUpperCase()}`;
    }

    //Updating menu state
    toggleMainMenu(menuName, action = "default") {

        //Checking if multiple menus can be drawn at once
        if (!this.uiSettings.drawMultipleMenus) {
            if (menuName === 'player') {
                this.uiState.isTileMenuActive = false;
            } else if (menuName === 'tile') {
                this.uiState.isPlayerMenuActive = false;
            }
        }
        
        //Fallback for tile submenu
        if (this.uiState.activeTileSubmenu === 'none') {
            this.uiState.activeTileSubmenu = 'sanctuary';
        }

        //Logic for updating state
        if (action === "default") {
            if (menuName === 'player') {
                this.uiState.isPlayerMenuActive = !this.uiState.isPlayerMenuActive;
            } else if (menuName === 'tile') {
                this.uiState.isTileMenuActive = !this.uiState.isTileMenuActive;
            }
        } else if (action === "open") {
            if (menuName === 'player') {
                this.uiState.isPlayerMenuActive = true;
            } else if (menuName === 'tile') {
                this.uiState.isTileMenuActive = true;
            }
        } else if (action === "close") {
            if (menuName === 'player') {
                this.uiState.isPlayerMenuActive = false;
            } else if (menuName === 'tile') {
                this.uiState.isTileMenuActive = false;
            }
        }
        
        //Draw updated state
        this.applyUiState();
    }

    //Updating submenu state
    toggleSubmenu(menuParent, subKey, action = "default") {
        if (menuParent === "player") {
            this.uiState.activePlayerSubmenu = subKey;
            this.setPlayerDropdownTitle(subKey);
            if (action === "open") {
                this.toggleMainMenu("player", "open");
            }    
        } else if (menuParent === "tile") {
            this.uiState.activeTileSubmenu = subKey;
            if (this.tileMenu) {
                this.tileMenu.forceUpdateSubmenu(this.uiState.activeTileSubmenu);
            }
            if (action === "open") {
                this.toggleMainMenu("tile", 'open');
            }    
        }

        //Draw updated state
        this.applyUiState();
    }

    //Updating state to close all current menus and then call a draw
    closeAllMenus() {
        this.uiState.isPlayerMenuActive = false;
        this.uiState.activePlayerSubmenu = "none";
        this.uiState.isTileMenuActive = false;
        this.uiState.activeTileSubmenu = "none";
        this.applyUiState();
    }

    //Central UI updating function
    update(dt) {
        if (this.state.uiInfo.swapPages) {
            this.state.uiInfo.swapPages = false;
            this.swapPage();
        }

        //Tile updating
        if (this.uiState.isTileMenuActive) {
            this.tileMenu.updateUI(this.uiState);
        }

        if (this.uiState.isPlayerMenuActive) {
            this.playerMenu.updateUI(this.uiState);
        }

        this.navigationMenu.updateUI();
    }
}